import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoggingService } from '../shared/services/logging';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
    AuthorizeNetCreateSubscriptionsFromPaymentParams,
    AuthorizeNetCreateSubscriptionsFromPaymentResponse,
    AuthorizeNetCancelSubscriptionResponse,
    AuthorizeNetRecordAdminLicensePurchaseResponse,
    AuthorizeNetRecordAdminLicenseRemovalResponse,
    AuthorizeNetCreateSubscriptionFromCustomerProfileParams,
    AuthorizeNetCreateSubscriptionFromCustomerProfileResponse,
    AuthorizeNetCustomerProfileParams,
    AuthorizeNetCustomerProfileResponse,
    AuthorizeNetSettledBatchListParams,
    AuthorizeNetSettledBatchListResponse,
    AuthorizeNetSubscriptionListParams,
    AuthorizeNetSubscriptionListResponse,
    AuthorizeNetSubscriptionResponse,
    AuthorizeNetTransactionDetailsResponse,
    AuthorizeNetTransactionListParams,
    AuthorizeNetTransactionListResponse,
    AuthorizeNetUnsettledTransactionListParams,
    AuthorizeNetUnsettledTransactionListResponse
} from './IAuthorizeNetSubscriptions';

@Injectable({
    providedIn: 'root'
})
export class AuthorizenetCheckoutService {
    private authorizeNetApiUri = `${environment.apiLocation}/AuthorizeNet`;
    private uri = `https://api.authorize.net/xml/v1/request.api`;

    constructor(
        @Inject(HttpClient) private http: HttpClient,
        @Inject(LoggingService) private log: LoggingService
    ) {}

    public post(creds: any): Observable<Array<any>> {
        return this.http.post<Array<any>>(this.uri, creds).pipe(res => {
            this.log.verbose({
                type: 'AuthorizenetCheckoutService:post:reponse',
                res
            });
            return res;
        });
    }

    public getSubscriptions(
        params: AuthorizeNetSubscriptionListParams
    ): Observable<AuthorizeNetSubscriptionListResponse> {
        const httpParams = new HttpParams()
            .set('searchType', params.searchType)
            .set('orderBy', params.orderBy)
            .set('orderDescending', String(params.orderDescending))
            .set('limit', String(params.limit))
            .set('offset', String(params.offset));

        return this.http
            .get<AuthorizeNetSubscriptionListResponse>(
                `${this.authorizeNetApiUri}/subscriptions`,
                { params: httpParams }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:getSubscriptions:response',
                        res
                    });
                })
            );
    }

    public getSubscription(
        subscriptionId: string,
        includeTransactions = true
    ): Observable<AuthorizeNetSubscriptionResponse> {
        const httpParams = new HttpParams()
            .set('subscriptionId', subscriptionId)
            .set('includeTransactions', String(includeTransactions));

        return this.http
            .get<AuthorizeNetSubscriptionResponse>(
                `${this.authorizeNetApiUri}/subscription`,
                { params: httpParams }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:getSubscription:response',
                        res
                    });
                })
            );
    }

    public getCustomerProfile(
        params: AuthorizeNetCustomerProfileParams
    ): Observable<AuthorizeNetCustomerProfileResponse> {
        let httpParams = new HttpParams();

        if (params.customerProfileId) {
            httpParams = httpParams.set('customerProfileId', params.customerProfileId);
        }
        if (params.merchantCustomerId) {
            httpParams = httpParams.set('merchantCustomerId', params.merchantCustomerId);
        }
        if (params.email) {
            httpParams = httpParams.set('email', params.email);
        }
        if (params.unmaskExpirationDate != null) {
            httpParams = httpParams.set(
                'unmaskExpirationDate',
                String(params.unmaskExpirationDate)
            );
        }
        if (params.includeIssuerInfo != null) {
            httpParams = httpParams.set('includeIssuerInfo', String(params.includeIssuerInfo));
        }

        return this.http
            .get<AuthorizeNetCustomerProfileResponse>(
                `${this.authorizeNetApiUri}/customer-profile`,
                { params: httpParams }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:getCustomerProfile:response',
                        res
                    });
                })
            );
    }

    public getTransactionList(
        params: AuthorizeNetTransactionListParams
    ): Observable<AuthorizeNetTransactionListResponse> {
        const httpParams = new HttpParams()
            .set('batchId', params.batchId)
            .set('orderBy', params.orderBy)
            .set('orderDescending', String(params.orderDescending))
            .set('limit', String(params.limit))
            .set('offset', String(params.offset));

        return this.http
            .get<AuthorizeNetTransactionListResponse>(
                `${this.authorizeNetApiUri}/transaction-list`,
                { params: httpParams }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:getTransactionList:response',
                        res
                    });
                })
            );
    }

    public getTransactionDetails(transId: string): Observable<AuthorizeNetTransactionDetailsResponse> {
        const httpParams = new HttpParams().set('transId', transId);

        return this.http
            .get<AuthorizeNetTransactionDetailsResponse>(
                `${this.authorizeNetApiUri}/transaction-details`,
                { params: httpParams }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:getTransactionDetails:response',
                        res
                    });
                })
            );
    }

    public getUnsettledTransactionList(
        params: AuthorizeNetUnsettledTransactionListParams
    ): Observable<AuthorizeNetUnsettledTransactionListResponse> {
        const httpParams = new HttpParams()
            .set('orderBy', params.orderBy)
            .set('orderDescending', String(params.orderDescending))
            .set('limit', String(params.limit))
            .set('offset', String(params.offset));

        return this.http
            .get<AuthorizeNetUnsettledTransactionListResponse>(
                `${this.authorizeNetApiUri}/unsettled-transaction-list`,
                { params: httpParams }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:getUnsettledTransactionList:response',
                        res
                    });
                })
            );
    }

    public createSubscriptionFromCustomerProfile(
        params: AuthorizeNetCreateSubscriptionFromCustomerProfileParams
    ): Observable<AuthorizeNetCreateSubscriptionFromCustomerProfileResponse> {
        let body = new HttpParams()
            .set('customerProfileId', params.customerProfileId)
            .set('customerPaymentProfileId', params.customerPaymentProfileId)
            .set('customerAddressId', params.customerAddressId)
            .set('subscriptionName', params.subscriptionName)
            .set('amount', String(params.amount))
            .set('startDate', params.startDate)
            .set('intervalLength', String(params.intervalLength))
            .set('intervalUnit', params.intervalUnit)
            .set('totalOccurrences', String(params.totalOccurrences));

        if (params.trialAmount != null) {
            body = body.set('trialAmount', String(params.trialAmount));
        }
        if (params.trialOccurrences != null) {
            body = body.set('trialOccurrences', String(params.trialOccurrences));
        }

        return this.http
            .post<AuthorizeNetCreateSubscriptionFromCustomerProfileResponse>(
                `${this.authorizeNetApiUri}/subscription-from-customer-profile`,
                body,
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:createSubscriptionFromCustomerProfile:response',
                        res
                    });
                })
            );
    }

    public createSubscriptionsFromPayment(
        params: AuthorizeNetCreateSubscriptionsFromPaymentParams
    ): Observable<AuthorizeNetCreateSubscriptionsFromPaymentResponse> {
        let body = new HttpParams()
            .set('dataDescriptor', params.dataDescriptor)
            .set('dataValue', params.dataValue)
            .set('merchantCustomerId', params.merchantCustomerId)
            .set('quantity', String(params.quantity))
            .set('unitAmount', String(params.unitAmount));

        if (params.subscriptionNamePrefix) {
            body = body.set('subscriptionNamePrefix', params.subscriptionNamePrefix);
        }
        body = body.set('isCancel', 'false');
        if (params.emailAddr?.trim()) {
            body = body.set('emailAddr', params.emailAddr.trim());
        }
        if (params.emailForCloudLogin?.trim()) {
            body = body.set('emailForCloudLogin', params.emailForCloudLogin.trim());
        }
        if (params.email) {
            body = body.set('email', params.email);
        }
        if (params.companyName) {
            body = body.set('companyName', params.companyName);
        }
        if (params.address1) {
            body = body.set('address1', params.address1);
        }
        if (params.address2) {
            body = body.set('address2', params.address2);
        }
        if (params.address3) {
            body = body.set('address3', params.address3);
        }
        if (params.city) {
            body = body.set('city', params.city);
        }
        if (params.state) {
            body = body.set('state', params.state);
        }
        if (params.zipCode) {
            body = body.set('zipCode', params.zipCode);
        }
        if (params.includeRightworksAccess) {
            body = body.set('includeRightworksAccess', 'true');
            if (params.rightworksUnitAmount != null) {
                body = body.set(
                    'rightworksUnitAmount',
                    String(params.rightworksUnitAmount)
                );
            }
            if (params.rightworksQuantity != null) {
                body = body.set(
                    'rightworksQuantity',
                    String(params.rightworksQuantity)
                );
            }
            if (params.rightworksSubscriptionNamePrefix) {
                body = body.set(
                    'rightworksSubscriptionNamePrefix',
                    params.rightworksSubscriptionNamePrefix
                );
            }
        }

        return this.http
            .post<AuthorizeNetCreateSubscriptionsFromPaymentResponse>(
                `${this.authorizeNetApiUri}/subscription-from-payment`,
                body,
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:createSubscriptionsFromPayment:response',
                        res
                    });
                })
            );
    }

    public recordAdminLicensePurchase(
        merchantCustomerId: string,
        quantity: number,
        subscriptionIds?: Array<string | number | null | undefined>,
        emailAddr?: string,
        emailForCloudLogin?: string,
        rightworksSubscriptionIds?: Array<string | number | null | undefined>
    ): Observable<AuthorizeNetRecordAdminLicensePurchaseResponse> {
        let body = new HttpParams()
            .set('merchantCustomerId', merchantCustomerId)
            .set('quantity', String(quantity))
            .set('isCancel', 'false');

        if (emailAddr?.trim()) {
            body = body.set('emailAddr', emailAddr.trim());
        }
        if (emailForCloudLogin?.trim()) {
            body = body.set('emailForCloudLogin', emailForCloudLogin.trim());
        }

        const normalizedSubscriptionIds = (subscriptionIds ?? [])
            .map(id => (id == null ? '' : String(id).trim()))
            .filter(id => id !== '');

        if (normalizedSubscriptionIds.length > 0) {
            body = body.set('subscriptionIds', normalizedSubscriptionIds.join(','));
        }

        const normalizedRightworksSubscriptionIds = (rightworksSubscriptionIds ?? [])
            .map(id => (id == null ? '' : String(id).trim()))
            .filter(id => id !== '');

        if (normalizedRightworksSubscriptionIds.length > 0) {
            body = body.set(
                'rightworksSubscriptionIds',
                normalizedRightworksSubscriptionIds.join(',')
            );
        }

        return this.http
            .post<AuthorizeNetRecordAdminLicensePurchaseResponse>(
                `${this.authorizeNetApiUri}/record-admin-license-purchase`,
                body,
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:recordAdminLicensePurchase:response',
                        res
                    });
                })
            );
    }

    public recordAdminLicenseRemoval(
        merchantCustomerId: string,
        quantity: number,
        emailAddr?: string,
        emailForCloudLogin?: string,
        subscriptionIds?: Array<string | number | null | undefined>
    ): Observable<AuthorizeNetRecordAdminLicenseRemovalResponse> {
        let body = new HttpParams()
            .set('merchantCustomerId', merchantCustomerId)
            .set('quantity', String(quantity))
            .set('isCancel', 'true');

        if (emailAddr?.trim()) {
            body = body.set('emailAddr', emailAddr.trim());
        }
        if (emailForCloudLogin?.trim()) {
            body = body.set('emailForCloudLogin', emailForCloudLogin.trim());
        }

        const normalizedSubscriptionIds = (subscriptionIds ?? [])
            .map(id => (id == null ? '' : String(id).trim()))
            .filter(id => id !== '');

        if (normalizedSubscriptionIds.length > 0) {
            body = body.set('subscriptionIds', normalizedSubscriptionIds.join(','));
        }

        return this.http
            .post<AuthorizeNetRecordAdminLicenseRemovalResponse>(
                `${this.authorizeNetApiUri}/record-admin-license-removal`,
                body,
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:recordAdminLicenseRemoval:response',
                        res
                    });
                })
            );
    }

    public cancelSubscription(
        subscriptionId: string
    ): Observable<AuthorizeNetCancelSubscriptionResponse> {
        const body = new HttpParams().set('subscriptionId', subscriptionId);

        return this.http
            .post<AuthorizeNetCancelSubscriptionResponse>(
                `${this.authorizeNetApiUri}/cancel-subscription`,
                body,
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:cancelSubscription:response',
                        res
                    });
                })
            );
    }

    public getSettledBatchList(
        params: AuthorizeNetSettledBatchListParams
    ): Observable<AuthorizeNetSettledBatchListResponse> {
        let httpParams = new HttpParams().set(
            'includeStatistics',
            String(params.includeStatistics)
        );

        if (params.firstSettlementDate) {
            httpParams = httpParams.set('firstSettlementDate', params.firstSettlementDate);
        }
        if (params.lastSettlementDate) {
            httpParams = httpParams.set('lastSettlementDate', params.lastSettlementDate);
        }

        return this.http
            .get<AuthorizeNetSettledBatchListResponse>(
                `${this.authorizeNetApiUri}/settled-batch-list`,
                { params: httpParams }
            )
            .pipe(
                tap(res => {
                    this.log.verbose({
                        type: 'AuthorizenetCheckoutService:getSettledBatchList:response',
                        res
                    });
                })
            );
    }
}
